import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Lock, PlayCircle, CheckCircle2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useSeo, VIDEO_CATS, fmtDate, embedUrl, safeUrl } from "../lib/api";
import { useAuth } from "../context/AppContext";
import { PageHero, Section, Loading, Empty, Badge } from "../components/Common";
import { Progress } from "../components/ui/progress";

export function VideoList() {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("");
  const { user } = useAuth();
  useSeo("Eğitim Videoları", "Özel güvenlik temel dersleri, mevzuat, ilk yardım, yangın güvenliği ve sınav hazırlık videoları.");
  useEffect(() => { setItems(null); api.get("/videos", { params: { category: cat } }).then((r) => setItems(r.data)); }, [cat, user]);
  return (
    <>
      <PageHero eyebrow="Video Kütüphanesi" title="Eğitim Videoları" text="Herkese açık videoları izleyin; kursiyerlere özel içerikler için giriş yapın." />
      <Section>
        <div className="flex gap-2 overflow-x-auto pb-3 mb-6" role="tablist">
          {[["", "Tümü"], ...Object.entries(VIDEO_CATS)].map(([k, v]) => <button key={k} onClick={() => setCat(k)} data-testid={`video-cat-${k || "all"}`} className={`shrink-0 px-4 py-2 text-xs font-bold uppercase border ${cat === k ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300"}`}>{v}</button>)}
        </div>
        {!items ? <Loading /> : items.length ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((v) => (
              <Link key={v.id} to={v.locked ? "/kursiyer-girisi" : `/egitim-videolari/${v.id}`} className="card-ada group" data-testid={`video-card-${v.id}`}>
                <div className="aspect-video bg-zinc-900 relative overflow-hidden">
                  {v.cover && <img src={v.cover} alt={v.title} loading="lazy" className="w-full h-full object-cover opacity-80" />}
                  <span className="absolute inset-0 flex items-center justify-center text-white">{v.locked ? <Lock size={34} /> : <PlayCircle size={44} />}</span>
                  {v.duration && <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-0.5">{v.duration}</span>}
                </div>
                <div className="p-5">
                  <div className="flex gap-2 mb-2">{v.is_public ? <Badge tone="green">Herkese açık</Badge> : <Badge tone="dark">Kursiyerlere özel</Badge>}</div>
                  <h2 className="font-bold leading-snug">{v.title}</h2>
                  <p className="text-xs text-zinc-500 mt-2">{VIDEO_CATS[v.category]} {v.instructor && `• ${v.instructor}`}</p>
                </div>
              </Link>
            ))}
          </div>
        ) : <Empty text="Bu kategoride yayınlanmış video bulunmuyor." />}
      </Section>
    </>
  );
}

export function VideoDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");
  const [pct, setPct] = useState(0);
  useEffect(() => {
    api.get(`/videos/${id}`).then((r) => { setV(r.data); setPct(r.data.progress?.percent || 0); }).catch((e) => setErr(errMsg(e)));
  }, [id]);
  useEffect(() => {
    if (!user || !v) return;
    api.post(`/videos/${id}/progress`, { percent: 10 }).then((r) => setPct(r.data.percent)).catch(() => {});
  }, [user, v, id]);
  useSeo(v?.title, v?.description);
  const complete = async () => {
    try { const r = await api.post(`/videos/${id}/progress`, { percent: 100 }); setPct(r.data.percent); toast.success("Ders tamamlandı olarak işaretlendi"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (err) return <Section><Empty text={err} testid="video-error" /><Link to="/kursiyer-girisi" className="btn-red mt-4">Kursiyer Girişi</Link></Section>;
  if (!v) return <Loading />;
  const src = embedUrl(v.video_url);
  return (
    <Section>
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div>
          <div className="aspect-video bg-black" data-testid="video-player">
            {src ? <iframe src={src} title={v.title} className="w-full h-full" allow="accelerometer; encrypted-media; picture-in-picture; fullscreen" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" /> : <div className="h-full flex items-center justify-center text-zinc-400 text-sm">Video bağlantısı henüz eklenmedi.</div>}
          </div>
          <h1 className="mt-5 text-2xl sm:text-3xl font-black">{v.title}</h1>
          <p className="text-sm text-zinc-500 mt-1">{VIDEO_CATS[v.category]} • {fmtDate(v.published_at)} {v.duration && `• ${v.duration}`} {v.instructor && `• Eğitmen: ${v.instructor}`}</p>
          <p className="mt-4 text-zinc-700 whitespace-pre-line">{v.description}</p>
        </div>
        <aside className="grid gap-4 content-start">
          {user ? (
            <div className="card-ada p-5" data-testid="video-progress-box">
              <p className="font-bold text-sm mb-2">İlerleme: %{pct}</p>
              <Progress value={pct} className="h-2" />
              <button className="btn-red w-full mt-4" onClick={complete} disabled={pct >= 100} data-testid="video-complete-btn"><CheckCircle2 size={16} />{pct >= 90 ? "Tamamlandı" : "Dersi tamamladım"}</button>
            </div>
          ) : <div className="card-ada p-5 text-sm">İlerlemenizi kaydetmek için <Link to="/kursiyer-girisi" className="underline font-bold">giriş yapın</Link>.</div>}
          {v.related_exam_id && <Link to={`/deneme-sinavlari/${v.related_exam_id}`} className="btn-dark" data-testid="video-related-exam">İlgili teste git</Link>}
          {v.material_url && <a href={safeUrl(v.material_url)} target="_blank" rel="noreferrer" className="btn-outline text-zinc-900 hover:!bg-zinc-950 hover:!text-white" data-testid="video-material"><FileDown size={16} />Ders materyali</a>}
          <Link to="/egitim-videolari" className="text-sm underline">← Video kütüphanesi</Link>
        </aside>
      </div>
    </Section>
  );
}
